
import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/auth/useAuth";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useAuthRedirect } from "@/hooks/auth/useAuthRedirect";

export const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [emailNotFoundError, setEmailNotFoundError] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { signIn, resetPassword } = useAuth();
  const { redirectAfterLogin } = useAuthRedirect();
  
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    
    // Reset error states
    setLoading(true);
    setEmailNotFoundError(false);
    setGeneralError(null);
    
    try {
      console.log("Attempting to sign in with email:", email);
      const result = await signIn({ email, password });
      
      if (result && !result.error) {
        console.log("Login successful, redirecting to account");
        redirectAfterLogin();
      }
    } catch (error: any) {
      console.error("Login error:", error);
      
      // Handle specific errors
      if (error.message?.toLowerCase().includes('email') && 
          error.message?.toLowerCase().includes('not found')) {
        setEmailNotFoundError(true);
      } else if (error.message?.toLowerCase().includes('locked')) {
        setGeneralError(error.message);
      } else if (error.message?.toLowerCase().includes('credentials')) {
        setGeneralError("Incorrect email or password. Please try again.");
      } else {
        setGeneralError(error.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    
    try {
      await resetPassword(resetEmail);
      setShowResetDialog(false);
    } catch (error) {
      // Error is handled inside the hook
    }
  };
  
  
  const redirectToSignup = () => {
    localStorage.setItem("signup_email", email);
    navigate("/register");
  };
  
  return (
    <>
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Welcome back</CardTitle>
          <CardDescription className="text-center">
            Sign in to access your knitting patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailNotFoundError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Account not found</AlertTitle>
              <AlertDescription>
                We couldn't find an account with this email address.
                <Button 
                  variant="link" 
                  className="p-0 h-auto ml-1 text-destructive"
                  onClick={redirectToSignup}
                >
                  Create an account?
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {generalError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Login failed</AlertTitle>
              <AlertDescription>{generalError}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleLogin}>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  required
                  value={email}
                  disabled={loading}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  disabled={loading}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
              
              <div className="flex justify-end items-center">
                <Button 
                  type="button" 
                  variant="link" 
                  className="px-0 text-sm"
                  onClick={() => {
                    setResetEmail(email);
                    setShowResetDialog(true);
                  }}
                >
                  Forgot your password?
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col items-center gap-4">
          <div className="text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Button variant="link" className="p-0" onClick={() => navigate("/register")}>
              Sign up
            </Button>
          </div>
        </CardFooter>
      </Card>

      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="name@example.com"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit">
                Send Reset Link
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
